/**
 * La fiche d'un utilisateur : ce qu'on peut y lire, et ce qu'on peut y écrire.
 *
 * ## Pourquoi ces règles sont ici
 *
 * Elles existaient déjà, mais dans le `onSubmit` de `ProfilePage.tsx` — et
 * elles n'avaient jamais été éprouvées. Un écran d'administration qui les
 * réécrirait à sa façon finirait par accepter ce que l'espace parent refuse.
 * Une seule définition, éprouvée une fois.
 *
 * ## Deux champs que ce module ne touche pas, et pourquoi
 *
 * **`role`** — il se change par le RPC `admin_change_user_role`, qui fait
 * davantage qu'un `update`. Un `.update({ role })` direct contournerait ce
 * qu'il fait en plus, sans rien dire.
 *
 * **`newsletter`** — l'abonnement vit dans les fonctions edge
 * `subscribe-newsletter` et `unsubscribe-newsletter`. Écrire la colonne seule
 * ferait dire « abonné » à une fiche dont le parent ne recevrait rien — ou
 * l'inverse, ce qui est pire.
 *
 * Les deux sont donc affichés en lecture, avec le chemin qui les modifie.
 */

/** Les champs qu'un administrateur peut modifier sur la fiche d'un parent. */
export interface Fiche {
  email: string;
  prenom: string | null;
  nom: string | null;
  adresse: string | null;
  cpostal: string | null;
  localite: string | null;
  telephone: string | null;
  telephone2: string | null;
  nnational: string | null;
  accountType: 'parent' | 'organisation' | 'other';
  organisationName: string | null;
  companyNumber: string | null;
  isLegalGuardian: boolean;
}

/** Ce qui ne se modifie pas ici, et qu'on affiche quand même. */
export interface FicheEnLecture {
  id: string;
  role: string;
  newsletter: boolean;
  creeLe: string | null;
}

/**
 * Les valeurs vides que la base a reçues au fil des imports.
 *
 * `'EMPTY'`, `'000'` et `'NULL'` sont des chaînes REELLES, pas des hypothèses :
 * l'espace parent les neutralise déjà à l'enregistrement. Les laisser passer
 * ferait échouer la validation du numéro national sur une valeur que personne
 * n'a saisie.
 */
const VIDES = new Set(['', 'EMPTY', '000', 'NULL', 'N/A', '-']);

function vide(v: string | null | undefined): string | null {
  const t = (v ?? '').trim();
  return VIDES.has(t.toUpperCase()) ? null : t;
}

/** Nettoie une fiche avant validation ou écriture. */
export function normaliserFiche(f: Fiche): Fiche {
  const type = f.accountType;
  return {
    email: (f.email ?? '').trim().toLowerCase(),
    prenom: vide(f.prenom),
    nom: vide(f.nom),
    adresse: vide(f.adresse),
    cpostal: vide(f.cpostal),
    localite: vide(f.localite),
    telephone: vide(f.telephone),
    telephone2: vide(f.telephone2),
    nnational: vide(f.nnational),
    accountType: type,
    /*
      Un compte qui n'est pas une organisation ne garde pas un nom
      d'organisation : la donnée survivrait au changement de type et
      ressortirait ailleurs, dans une facture ou une attestation.
    */
    organisationName: type === 'organisation' ? vide(f.organisationName) : null,
    companyNumber: type === 'organisation' ? vide(f.companyNumber) : null,
    isLegalGuardian: f.isLegalGuardian,
  };
}

export interface Reproche {
  champ: keyof Fiche;
  message: string;
}

/**
 * Ce qui empêche d'enregistrer.
 *
 * Volontairement court : une fiche d'administration sert à CORRIGER des
 * données déjà imparfaites. Exiger ici ce que le formulaire d'inscription
 * exige rendrait inmodifiables précisément les fiches qu'on veut réparer.
 */
export function validerFiche(f: Fiche): Reproche[] {
  const n = normaliserFiche(f);
  const reproches: Reproche[] = [];

  if (!n.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(n.email)) {
    reproches.push({ champ: 'email', message: 'Une adresse de courriel valide est nécessaire.' });
  }

  if (n.accountType === 'organisation' && !n.organisationName) {
    reproches.push({
      champ: 'organisationName',
      message: "Un compte d'organisation doit porter le nom de l'organisation.",
    });
  }

  if (n.cpostal && !/^\d{4}$/.test(n.cpostal)) {
    reproches.push({ champ: 'cpostal', message: 'Le code postal belge compte quatre chiffres.' });
  }

  return reproches;
}

/**
 * Ce qui a changé, et rien d'autre.
 *
 * Envoyer la fiche entière écraserait une colonne qu'un autre écran vient de
 * modifier — l'espace parent, un import, une fonction edge. On n'écrit que ce
 * qu'on a effectivement touché.
 */
export function champsModifies(avant: Fiche, apres: Fiche): Partial<Fiche> {
  const a = normaliserFiche(avant);
  const b = normaliserFiche(apres);
  const diff: Partial<Fiche> = {};
  for (const cle of Object.keys(b) as (keyof Fiche)[]) {
    if (a[cle] !== b[cle]) (diff as Record<string, unknown>)[cle] = b[cle];
  }
  return diff;
}

/** Le nom lisible d'un compte, quel que soit ce qui est rempli. */
export function nomAffiche(f: Pick<Fiche, 'accountType' | 'organisationName' | 'prenom' | 'nom' | 'email'>): string {
  if (f.accountType === 'organisation' && f.organisationName) return f.organisationName;
  const nom = [f.prenom, f.nom].filter(Boolean).join(' ').trim();
  return nom || f.email || 'Compte sans nom';
}
